/**
 * Billing review's entry drawer: the released entry, what it's worth, how it
 * changed, and the admin's actions. Raw captured activity is not shown; it
 * stays private to the timekeeper.
 */
import { AlertTriangle, Check, Lock, Undo2 } from "lucide-react";
import type { FC } from "react";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

import { reviewActions, timeActions } from "../actions";
import { FlatFeeTag } from "../board/BoardCard";
import { CaseDot } from "../components/CasePicker";
import { longDate, utbmsLabel } from "../model";
import { useTimeEntry } from "../queries";
import type { TimeEntry } from "../types";
import { useHeld } from "../useHeld";
import { formatHours } from "../utils";
import {
  amountDetail,
  canApprove,
  canReturn,
  historyLine,
  openErrors
} from "./reviewModel";
import { ReviewStatus } from "./ReviewStatus";

const stamp = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
};

type Props = {
  /** The row as listed; the full entry with history loads on open. */
  row: TimeEntry | null;
  onClose: () => void;
  onReturn: (entry: TimeEntry) => void;
};

export const ReviewEntrySheet: FC<Props> = ({ row, onClose, onReturn }) => {
  const shown = useHeld(row);
  const query = useTimeEntry(shown?.id ?? null);
  const entry = query.data ?? shown;
  const names = {
    ...(query.data?.history_names ?? {}),
    ...(entry?.user_name ? { [entry.user_id]: entry.user_name } : {})
  };
  const history = query.data?.history ?? [];
  const errors = entry ? openErrors(entry) : [];
  const firstName = entry?.user_name?.split(" ")[0] ?? "them";

  return (
    <Sheet open={Boolean(row)} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        className="w-110 gap-0 p-0 sm:max-w-110"
        data-testid="review-entry-sheet"
      >
        {entry ? (
          <>
            <SheetHeader className="flex-row items-center gap-2 border-b border-line-2 px-4 py-3">
              <SheetTitle className="text-sm">Time Entry</SheetTitle>
              <ReviewStatus entry={entry} />
              <SheetDescription className="sr-only">
                {entry.case_label} on {entry.work_date}
              </SheetDescription>
            </SheetHeader>

            <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
              <dl className="grid grid-cols-[96px_minmax(0,1fr)] gap-y-2.5 text-base">
                <dt className="text-fg-3">Timekeeper</dt>
                <dd>{entry.user_name || "Unknown"}</dd>
                <dt className="text-fg-3">Case</dt>
                <dd className="flex min-w-0 items-center gap-2">
                  {entry.individual_id ? (
                    <>
                      <CaseDot id={entry.individual_id} />
                      <span className="truncate">{entry.case_label}</span>
                      <FlatFeeTag entry={entry} />
                    </>
                  ) : (
                    "None"
                  )}
                </dd>
                <dt className="text-fg-3">Date</dt>
                <dd>{longDate(entry.work_date)}</dd>
                <dt className="text-fg-3">Activity</dt>
                <dd>
                  {entry.utbms_activity ? (
                    <>
                      <span className="font-mono text-sm">
                        {entry.utbms_activity}
                      </span>{" "}
                      {utbmsLabel(entry.utbms_activity)}
                    </>
                  ) : (
                    <span className="text-fg-3">No code</span>
                  )}
                </dd>
                <dt className="text-fg-3">Hours</dt>
                <dd className="font-medium tabular-nums">
                  {formatHours(entry.duration_hours)}
                </dd>
                <dt className="text-fg-3">Amount</dt>
                <dd className="tabular-nums">{amountDetail(entry)}</dd>
              </dl>

              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-fg-3">Narrative</span>
                {canReturn(entry) ? (
                  <Textarea
                    key={`${entry.id}-${entry.narrative}`}
                    aria-label="Narrative"
                    defaultValue={entry.narrative}
                    placeholder="Write the invoice description"
                    className="min-h-16 text-base leading-4.75"
                    onBlur={(e) => {
                      const next = e.currentTarget.value.trim();
                      if (next && next !== entry.narrative)
                        void timeActions.update(entry.id, { narrative: next });
                    }}
                  />
                ) : (
                  <p className="rounded-lg border border-line-2 bg-fill-1 px-2.5 py-2 text-base leading-4.75">
                    {entry.narrative || (
                      <span className="text-fg-3">No narrative</span>
                    )}
                  </p>
                )}
              </div>

              {entry.status === "returned" && entry.return_note ? (
                <div className="rounded-lg border border-danger-line bg-danger-soft px-3 py-2 text-sm text-danger">
                  Returned: {entry.return_note}
                </div>
              ) : null}

              {errors.length ? (
                <div className="flex flex-col gap-1.5">
                  {errors.map((f) => (
                    <div
                      key={f.id}
                      className="flex gap-2 rounded-lg border border-warn-line bg-warn-soft px-3 py-2 text-sm text-warn"
                    >
                      <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                      <span>
                        <span className="font-medium">{f.label}.</span>{" "}
                        {f.message}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-fg-3">History</span>
                {query.isLoading ? (
                  <Skeleton className="h-16 w-full" />
                ) : history.length ? (
                  <ol className="flex flex-col" data-testid="review-history">
                    {history.map((h, i) => {
                      const line = historyLine(h, names);
                      const last = i === history.length - 1;
                      return (
                        <li
                          // eslint-disable-next-line react/no-array-index-key
                          key={`${h.at}-${i}`}
                          className="relative grid grid-cols-[14px_minmax(0,1fr)] gap-2.5 pb-2.5"
                        >
                          <span
                            className={
                              last
                                ? "mt-1.5 ml-0.75 size-1.75 rounded-full bg-primary"
                                : "mt-1.5 ml-0.75 size-1.75 rounded-full bg-border"
                            }
                          />
                          {!last ? (
                            <span className="absolute top-3.75 bottom-0 left-1.5 w-px bg-border" />
                          ) : null}
                          <span className="flex flex-col">
                            <span className="text-base">
                              <span className="font-medium">{line.who}</span>{" "}
                              <span className="text-fg-3">{line.text}</span>
                            </span>
                            <span className="text-sm text-fg-3">
                              {stamp(h.at)}
                            </span>
                          </span>
                        </li>
                      );
                    })}
                  </ol>
                ) : (
                  <span className="text-sm text-fg-3">
                    No changes recorded.
                  </span>
                )}
              </div>

              <p className="flex gap-2 text-sm leading-4.25 text-fg-3">
                <Lock className="mt-0.5 size-3 shrink-0" />
                Raw captured activity stays private to the timekeeper. Admins
                see the released entry and how it changed.
              </p>
            </div>

            {entry.status === "exported" ? (
              <SheetFooter className="flex-row items-center gap-1.5 border-t border-line-2 p-3 text-sm text-fg-3">
                <Lock className="size-3" /> Locked by an export. Void the batch
                to make changes.
              </SheetFooter>
            ) : canReturn(entry) ? (
              <SheetFooter className="flex-row gap-2 border-t border-line-2 p-3">
                {canApprove(entry) ? (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={async () => {
                      const out = await reviewActions.approve(
                        [entry.id],
                        errors.length > 0
                      );
                      if (out) onClose();
                    }}
                  >
                    <Check />
                    {errors.length ? "Approve anyway" : "Approve"}
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => onReturn(entry)}
                >
                  <Undo2 />
                  Return to {firstName}
                </Button>
              </SheetFooter>
            ) : null}
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
};
